import { useState } from 'react';
import type { NewsEvent } from '@contracts/news';
import { NewsPolicyBanner, useNews } from '../components/NewsTimeline';
import { Banner, Empty, Panel } from '../components/Panel';
import { useNow } from '../hooks/useNow';
import { dash, dateTime, humanize, relative } from '../lib/format';

const IMPACTS: NewsEvent['impact'][] = ['high', 'medium', 'low'];

export function NewsPage() {
  const { data, isLoading, isError } = useNews(24, 168);
  const now = useNow(15_000);
  const [impacts, setImpacts] = useState<Set<string>>(new Set(['high', 'medium', 'low']));
  const events = (data?.events ?? []).filter((e) => impacts.has(e.impact));

  const toggle = (impact: string) =>
    setImpacts((prev) => {
      const next = new Set(prev);
      if (next.has(impact)) next.delete(impact);
      else next.add(impact);
      return next;
    });

  return (
    <>
      <div className="page__header">
        <h1>News &amp; economic events</h1>
        <p className="page__lede">
          Scheduled releases and unscheduled headlines that matter for gold, and how the risk policy treats them.
        </p>
      </div>
      {data?.isDemo ? (
        <Banner tone="info" title="DEMO calendar">
          No news API key is configured, so this is a realistic demo calendar. Event times and values are illustrative,
          not real releases.
        </Banner>
      ) : null}
      <NewsPolicyBanner data={data} />
      <Panel
        title="Calendar"
        meta={
          <span className="row" role="group" aria-label="Filter by impact">
            {IMPACTS.map((i) => (
              <label key={i} className="row small">
                <input type="checkbox" checked={impacts.has(i)} onChange={() => toggle(i)} />
                {humanize(i)}
              </label>
            ))}
          </span>
        }
      >
        {isLoading ? <p className="muted">Loading calendar…</p> : null}
        {isError ? <p className="muted">The news service is unavailable.</p> : null}
        {data && !events.length ? <Empty title="No events match">Try including more impact levels.</Empty> : null}
        {events.length ? (
          <div className="table-wrap">
            <table className="ledger">
              <caption className="visually-hidden">Economic events and headlines</caption>
              <thead>
                <tr>
                  <th scope="col">Time</th>
                  <th scope="col">Event</th>
                  <th scope="col">Impact</th>
                  <th scope="col">Phase</th>
                  <th scope="col" className="r">
                    Actual
                  </th>
                  <th scope="col" className="r">
                    Forecast
                  </th>
                  <th scope="col" className="r">
                    Previous
                  </th>
                  <th scope="col">Received</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td className="num">
                      {dateTime(e.eventTime)}
                      <div className="small muted">{relative(e.eventTime, now)}</div>
                    </td>
                    <td>
                      <strong>{e.title}</strong>
                      <div className="small muted">
                        {e.currencies.join(', ')} · {e.kind === 'unscheduled' ? 'headline' : 'scheduled'}
                        {e.isDemo ? ' · demo' : ''}
                      </div>
                      {e.goldRelevance ? <div className="small muted">{e.goldRelevance}</div> : null}
                    </td>
                    <td>{humanize(e.impact)}</td>
                    <td>{humanize(e.phase)}</td>
                    <td className="r num">{e.actual ?? dash}</td>
                    <td className="r num">{e.forecast ?? dash}</td>
                    <td className="r num">{e.previous ?? dash}</td>
                    <td className="small">{dateTime(e.receivedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Panel>
      <h2 className="section-title">Policy</h2>
      <div className="grid grid--2">
        <Panel title="High impact (USD / XAU)">
          <p className="small">
            Marked as a high-impact window from 15 minutes before to 15 minutes after the release (configurable),
            then as elevated risk for up to an hour. Expect fast moves and wider spreads.
          </p>
        </Panel>
        <Panel title="Medium impact">
          <p className="small">
            Marked as elevated risk from 15 minutes before to 10 minutes after. Low-impact events are
            informational.
          </p>
        </Panel>
      </div>
    </>
  );
}
